import React, { useState } from "react";
import AddressAutocomplete from "./AddressAutoComplete";

export default function FacilityEditModal({
  mode = "add", // 'add' | 'edit'
  facility = null, // when editing: object { locationId?, ... }
  onClose,
  onSave, // async (values) => void
}) {
  const [form, setForm] = useState({
    locationId: facility?.locationId ?? facility?.id ?? null,
    facilityName: facility?.facilityName || "",
    facilityEmail: facility?.facilityEmail || "",
    facilityWebsite: facility?.facilityWebsite || "",
    addressLine: facility?.addressLine || "",
    city: facility?.city || "",
    state: facility?.state || "",
    country: facility?.country || "",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((s) => ({ ...s, [name]: value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    setSaving(true);
    try {
      const payload = {
        locationId: form.locationId ?? null, // present for edit
        facilityName: form.facilityName || null,
        facilityEmail: form.facilityEmail || null,
        facilityWebsite: form.facilityWebsite || null,
        addressLine: form.addressLine || null,
        city: form.city || null,
        state: form.state || null,
        country: form.country || null,
      };
      await onSave(payload);
    } catch (ex) {
      setErr(ex?.message || "Failed to save facility");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative z-10 w-full max-w-2xl rounded-xl bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            {mode === "edit" ? "Edit Facility" : "Add Facility"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-sm text-gray-700">Facility Name</span>
              <input
                name="facilityName"
                value={form.facilityName}
                onChange={onChange}
                className="mt-1 w-full rounded-md border px-3 py-2"
                placeholder="e.g. Austin General Hospital"
              />
            </label>
            <label className="block">
              <span className="text-sm text-gray-700">Facility Email</span>
              <input
                type="email"
                name="facilityEmail"
                value={form.facilityEmail}
                onChange={onChange}
                className="mt-1 w-full rounded-md border px-3 py-2"
                placeholder="e.g. info@hospital.com"
              />
            </label>
            <label className="block">
              <span className="text-sm text-gray-700">Facility Website</span>
              <input
                name="facilityWebsite"
                value={form.facilityWebsite}
                onChange={onChange}
                className="mt-1 w-full rounded-md border px-3 py-2"
                placeholder="e.g. hospital.com"
              />
            </label>
            <label className="block md:col-span-2">
              <span className="text-sm text-gray-700">Address Line</span>
              <AddressAutocomplete
                value={form.addressLine}
                onChange={(val) => setForm((s) => ({ ...s, addressLine: val }))}
                onSelect={(addr) => {
                  // normalize + default country
                  const nextCountry = addr.country || "United States";
                  setForm((s) => ({
                    ...s,
                    addressLine: addr.addressLine || s.addressLine,
                    city: addr.city || s.city,
                    state: addr.state || s.state,
                    country: nextCountry,
                  }));
                }}
                placeholder="Start typing an address…"
              />
            </label>
            <label className="block">
              <span className="text-sm text-gray-700">City</span>
              <input
                name="city"
                value={form.city}
                onChange={onChange}
                className="mt-1 w-full rounded-md border px-3 py-2"
              />
            </label>
            <label className="block">
              <span className="text-sm text-gray-700">State</span>
              <input
                name="state"
                value={form.state}
                onChange={onChange}
                className="mt-1 w-full rounded-md border px-3 py-2"
              />
            </label>
            <label className="block">
              <span className="text-sm text-gray-700">Country</span>
              <input
                name="country"
                value={form.country}
                onChange={onChange}
                className="mt-1 w-full rounded-md border px-3 py-2"
              />
            </label>
          </div>

          {err && <p className="text-sm text-red-600">{err}</p>}

          <div className="mt-4 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded border px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="rounded bg-emerald-600 px-4 py-2 text-sm text-white hover:bg-emerald-700 disabled:opacity-60"
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
